import { useQuery } from '@tanstack/react-query';
import isBefore from 'date-fns/isBefore';
import parseISO from 'date-fns/parseISO';
import ITrip from 'model/ITrip';
import { isValidDate } from 'utils/formatDate';
import { BOOKING_STATUS } from '~/constants';

export type TripRefunds = {
  fullRefundDateThreshold: Date | null;
  partialRefundDateThreshold: Date | null;
  noRefundDateThreshold: Date | null;
};

export enum RefundType {
  FULL = 'a full refund',
  PARTIAL = 'a partial refund',
  NONE = 'no refund',
}

export function getRefundDateThresholds(trip: ITrip): TripRefunds {
  const refundPolicies =
    trip?.trip_details?.financeDetails?.refundPolicies ?? [];
  const { partialRefundDateThreshold, fullRefundDateThreshold } =
    refundPolicies.reduce<{
      partialRefundDateThreshold: Date | null;
      fullRefundDateThreshold: Date | null;
    }>(
      (acc, policy) => {
        try {
          switch (policy.type) {
            case 'LessThan168HoursRefundPolicy':
              if (policy.latestRefundableCancellationDate) {
                acc.partialRefundDateThreshold = parseISO(
                  `${policy.latestRefundableCancellationDate}${trip.zoneOffset}`,
                );
              }
              break;
            case 'UpTo168HoursRefundPolicy':
              acc.partialRefundDateThreshold =
                parseISO(
                  `${policy.latestRefundableCancellationDate}${trip.zoneOffset}`,
                ) ?? null;
              break;
            case 'Window24HoursRefundPolicy':
              acc.fullRefundDateThreshold =
                parseISO(
                  `${policy.latestRefundableCancellationDate}${trip.zoneOffset}`,
                ) ?? null;
              break;
          }
        } catch (error: any) {
          console.error('Error parsing refund policies', error);
        }

        return acc;
      },
      {
        partialRefundDateThreshold: null,
        fullRefundDateThreshold: null,
      },
    );

  if (!partialRefundDateThreshold || !isValidDate(partialRefundDateThreshold)) {
    console.error(`No partial refund policy found for trip ${trip?.id}`);
  }
  if (!fullRefundDateThreshold || !isValidDate(fullRefundDateThreshold)) {
    console.error(`No full refund policy found for trip ${trip?.id}`);
  }
  return {
    fullRefundDateThreshold: isValidDate(fullRefundDateThreshold)
      ? fullRefundDateThreshold
      : null,
    partialRefundDateThreshold: isValidDate(partialRefundDateThreshold)
      ? partialRefundDateThreshold
      : null,
    noRefundDateThreshold: isValidDate(partialRefundDateThreshold)
      ? partialRefundDateThreshold
      : isValidDate(fullRefundDateThreshold)
        ? fullRefundDateThreshold
        : null,
  };
}

export const getRefundType = ({
  booking,
  refundThresholds,
}: {
  booking: ITrip;
  refundThresholds: TripRefunds | null | undefined;
}): RefundType => {
  if (booking?.status === BOOKING_STATUS.UNCONFIRMED) {
    return RefundType.FULL;
  }
  const now = new Date();
  if (
    refundThresholds?.fullRefundDateThreshold &&
    isBefore(now, refundThresholds.fullRefundDateThreshold)
  ) {
    return RefundType.FULL;
  }

  if (
    refundThresholds?.partialRefundDateThreshold &&
    isBefore(now, refundThresholds.partialRefundDateThreshold)
  ) {
    return RefundType.PARTIAL;
  }

  return RefundType.NONE;
};

export const useTripRefunds = (trip: ITrip) => {
  const query = useQuery<TripRefunds | null>({
    queryKey: ['trip', 'refund', trip?.id],
    queryFn: async () => {
      const refundDateThresholds = getRefundDateThresholds(trip);
      return Promise.resolve(refundDateThresholds).catch(() => null);
    },
    retry: false,
    enabled: !!trip?.id,
    refetchOnWindowFocus: false,
  });

  return {
    ...query,
    getRefundType: () =>
      getRefundType({ booking: trip, refundThresholds: query?.data }),
  };
};
